<template>
  <div class="lp">
    <!-- Templates section -->
    <div class="lp-header">
      <h2 class="lp-title">Start from a Template</h2>
      <p class="lp-subtitle">Pre-designed templates with sample content you can customize</p>
    </div>

    <!-- Filter dropdown -->
    <div class="lp-filter-row">
      <label class="lp-filter-label">Filter by:</label>
      <select v-model="activeFilter" class="lp-filter-select">
        <option value="all">All Templates</option>
        <option v-for="tag in filterTags" :key="tag" :value="tag">{{ tag }}</option>
      </select>
    </div>

    <div class="lp-grid">
      <div
        v-for="tpl in filteredTemplates"
        :key="tpl.id"
        class="lp-card"
        @click="$emit('select-template', tpl)"
      >
        <div class="lp-schematic">
          <div class="lp-s-tpl-icon">
            <i class="material-icons">description</i>
          </div>
        </div>
        <div class="lp-card-info">
          <div class="lp-card-name">{{ tpl.name }}</div>
          <div class="lp-card-desc">{{ tpl.description }}</div>
          <div class="lp-card-tags">
            <span v-for="tag in tpl.tags" :key="tag" class="lp-tag" :class="tagClass(tag)">{{ tag }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Blank layouts section -->
    <div class="lp-header lp-header--layouts">
      <h2 class="lp-title">Or Start from a Blank Layout</h2>
      <p class="lp-subtitle">Choose a layout and build your content from scratch</p>
    </div>
    <div class="lp-grid">
      <div
        v-for="layout in layouts"
        :key="layout.id"
        class="lp-card"
        @click="$emit('select-layout', layout.id)"
      >
        <div class="lp-schematic" :class="'lp-schematic--' + layout.id">
          <template v-if="layout.id === 'clean'">
            <div class="lp-s-line lp-s-line--70"></div>
            <div class="lp-s-line lp-s-line--100"></div>
            <div class="lp-s-line lp-s-line--100"></div>
            <div class="lp-s-line lp-s-line--85"></div>
            <div class="lp-s-btn"></div>
          </template>
          <template v-if="layout.id === 'hero'">
            <div class="lp-s-img"></div>
            <div class="lp-s-line lp-s-line--100"></div>
            <div class="lp-s-line lp-s-line--100"></div>
            <div class="lp-s-line lp-s-line--60"></div>
            <div class="lp-s-btn"></div>
          </template>
          <template v-if="layout.id === 'event'">
            <div class="lp-s-img"></div>
            <div class="lp-s-card">
              <div class="lp-s-line lp-s-line--50"></div>
              <div class="lp-s-line lp-s-line--70 lp-s-line--thin"></div>
              <div class="lp-s-line lp-s-line--60 lp-s-line--thin"></div>
            </div>
            <div class="lp-s-line lp-s-line--100"></div>
            <div class="lp-s-btn"></div>
          </template>
          <template v-if="layout.id === 'newsletter'">
            <div class="lp-s-line lp-s-line--60 lp-s-line--bold"></div>
            <div class="lp-s-section">
              <div class="lp-s-line lp-s-line--40"></div>
              <div class="lp-s-line lp-s-line--100 lp-s-line--thin"></div>
            </div>
            <div class="lp-s-divider"></div>
            <div class="lp-s-section">
              <div class="lp-s-line lp-s-line--40"></div>
              <div class="lp-s-line lp-s-line--100 lp-s-line--thin"></div>
            </div>
          </template>
          <template v-if="layout.id === 'welcome'">
            <div class="lp-s-line lp-s-line--50 lp-s-line--bold"></div>
            <div class="lp-s-line lp-s-line--100"></div>
            <div class="lp-s-line lp-s-line--90"></div>
            <div class="lp-s-checks">
              <div class="lp-s-check"></div>
              <div class="lp-s-check"></div>
              <div class="lp-s-check"></div>
            </div>
            <div class="lp-s-btn"></div>
          </template>
        </div>
        <div class="lp-card-info">
          <div class="lp-card-name">{{ layout.name }}</div>
          <div class="lp-card-desc">{{ layout.description }}</div>
          <div class="lp-card-tags">
            <span v-for="uc in layout.useCases" :key="uc" class="lp-tag">{{ uc }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { LAYOUTS, TEMPLATES } from '../../helpers/email-layouts';

var TAG_CLASSES = {
  Lead: 'lp-tag--lead',
  Student: 'lp-tag--student',
  Employer: 'lp-tag--employer',
  Marketing: 'lp-tag--marketing',
  Healthcare: 'lp-tag--healthcare',
  General: 'lp-tag--general',
};

export default {
  name: 'LayoutPicker',

  data() {
    return {
      layouts: LAYOUTS,
      templates: TEMPLATES,
      activeFilter: 'all',
    };
  },

  computed: {
    filterTags: function () {
      return ['Lead', 'Student', 'Employer', 'Marketing', 'Healthcare', 'General'];
    },

    filteredTemplates: function () {
      if (this.activeFilter === 'all') return this.templates;
      var filter = this.activeFilter;
      return this.templates.filter(function (tpl) {
        return tpl.tags && tpl.tags.indexOf(filter) !== -1;
      });
    },
  },

  methods: {
    tagClass: function (tag) {
      return TAG_CLASSES[tag] || '';
    },
  },
};
</script>

<style scoped>
.lp {
  max-width: 960px;
  margin: 0 auto;
  padding: 40px 24px;
}
.lp-header {
  text-align: center;
  margin-bottom: 20px;
}
.lp-header--layouts {
  margin-top: 40px;
  padding-top: 32px;
  border-top: 1px solid #e2e6ea;
}
.lp-title {
  font-size: 22px;
  font-weight: 700;
  color: #181818;
  margin: 0 0 6px 0;
}
.lp-subtitle {
  font-size: 14px;
  color: #5f6368;
  margin: 0;
}

/* Filter dropdown */
.lp-filter-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 24px;
}
.lp-filter-label {
  font-size: 14px;
  font-weight: 600;
  color: #5f6368;
}
.lp-filter-select {
  height: 38px;
  min-width: 180px;
  padding: 0 32px 0 12px;
  border: 2px solid #dadce0;
  border-radius: 8px;
  background: #fff;
  font-size: 14px;
  font-weight: 500;
  color: #181818;
  cursor: pointer;
  outline: none;
  transition: border-color 0.15s;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%235f6368'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 10px 6px;
}
.lp-filter-select:focus {
  border-color: var(--primary-color, #3b86f5);
}

/* Grid */
.lp-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
}

/* Card */
.lp-card {
  border: 2px solid #e2e6ea;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;
  overflow: hidden;
}
.lp-card:hover {
  border-color: var(--primary-color, #3b86f5);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

/* Schematic area */
.lp-schematic {
  background: #f4f3ed;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 120px;
  border-bottom: 1px solid #e2e6ea;
}

/* Template icon schematic */
.lp-s-tpl-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
}
.lp-s-tpl-icon .material-icons {
  font-size: 40px;
  color: var(--primary-color, #3b86f5);
  opacity: 0.5;
}

/* Schematic building blocks */
.lp-s-line {
  height: 6px;
  background: #c4c8d0;
  border-radius: 3px;
}
.lp-s-line--thin { height: 4px; background: #dadce0; }
.lp-s-line--bold { height: 8px; background: #9aa0a6; }
.lp-s-line--100 { width: 100%; }
.lp-s-line--90 { width: 90%; }
.lp-s-line--85 { width: 85%; }
.lp-s-line--70 { width: 70%; }
.lp-s-line--60 { width: 60%; }
.lp-s-line--50 { width: 50%; }
.lp-s-line--40 { width: 40%; }

.lp-s-img {
  height: 36px;
  background: #dadce0;
  border-radius: 4px;
}
.lp-s-btn {
  width: 60px;
  height: 12px;
  background: var(--primary-color, #3b86f5);
  border-radius: 6px;
  margin-top: 4px;
  align-self: center;
}
.lp-s-card {
  background: #e8eaed;
  border-radius: 4px;
  padding: 6px 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.lp-s-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.lp-s-divider {
  height: 1px;
  background: #dadce0;
  margin: 2px 0;
}
.lp-s-checks {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 2px;
}
.lp-s-check {
  height: 4px;
  width: 70%;
  background: #34a853;
  border-radius: 2px;
}

/* Card info */
.lp-card-info {
  padding: 14px 16px;
}
.lp-card-name {
  font-size: 15px;
  font-weight: 600;
  color: #181818;
  margin-bottom: 4px;
}
.lp-card-desc {
  font-size: 12px;
  color: #5f6368;
  line-height: 1.4;
  margin-bottom: 8px;
}
.lp-card-tags {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}
.lp-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
  background: #f4f3ed;
  color: #5f6368;
}
/* Tag colors by audience */
.lp-tag--lead { background: #e8f0fe; color: #1967d2; }
.lp-tag--student { background: #e6f4ea; color: #137333; }
.lp-tag--employer { background: #fef7e0; color: #b06000; }
.lp-tag--marketing { background: #fce8e6; color: #c5221f; }
.lp-tag--healthcare { background: #f3e8fd; color: #7627bb; }
.lp-tag--general { background: #f4f3ed; color: #5f6368; }
</style>
